import { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Input, Alert, Row, Col } from "reactstrap";
import ApiCall from '../utils/apis';
import { isSuperAdmin } from '../utils/session';

const GREEN = '#2dce89';
const RED = '#f5365c';

const styles = {
  card: (running) => ({
    background: '#111a33', borderRadius: 8, border: '1px solid #2a3558',
    borderTop: `3px solid ${running ? GREEN : '#fb8a9c'}`,
    padding: '16px 18px', color: '#c9d3ee', fontFamily: 'monospace', height: '100%',
    display: 'flex', flexDirection: 'column',
  }),
  coin: { background: '#26314f', color: '#fff', fontWeight: 700, borderRadius: 6, padding: '8px 10px', fontSize: 14, marginRight: 12 },
  title: { color: '#fff', fontWeight: 700, fontSize: 17 },
  tag: { background: '#26314f', color: '#c9d3ee', borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 700, marginLeft: 8, letterSpacing: '0.04em' },
  pill: (running) => ({
    border: `1px solid ${running ? GREEN : '#8898aa'}`, color: running ? GREEN : '#8898aa',
    background: running ? 'rgba(45,206,137,0.12)' : 'rgba(136,152,170,0.12)',
    borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap',
  }),
  muted: { color: '#8f9bbd', fontSize: 12 },
  action: { background: '#0c1429', borderRadius: 4, padding: '6px 10px', fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 },
  footer: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 28 },
  button: (color, border) => ({
    background: 'rgba(255,255,255,0.04)', color, border: `1px solid ${border}`, borderRadius: 4,
    padding: '5px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer',
  }),
  group: { border: '1px solid #2a3558', borderRadius: 10, padding: 16, marginBottom: 20, background: '#0b1226' },
  groupTitle: { color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 12, fontFamily: 'monospace' },
};

// Tarjeta de un bot. `actions` decide que botones se ofrecen (propio vs ajeno)
const BotCard = ({ bot, busy, onView, onStart, onStop, onDelete }) => {
  const running = bot.status === 'running';
  const working = busy === bot.id;
  const disabled = busy !== null;
  const summary = bot.summary;
  const dim = (style) => ({ ...style, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' });

  return (
    <div style={styles.card(running)}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <span style={styles.coin}>{bot.coin}</span>
        <div style={{ flex: 1 }}>
          <div>
            <span style={styles.title}>{bot.coin} / USDT</span>
            <span style={styles.tag}>{bot.amount} USDT</span>
            {bot.compoundCap && (
              <span style={styles.tag} title="Interés compuesto: opera con el monto más la ganancia realizada, hasta el tope">
                compuesto {bot.tradeAmount} / {bot.compoundCap} USDT
              </span>
            )}
          </div>
          <div style={styles.muted}>Estrategia: <b style={{ color: '#c9d3ee' }}>{bot.strategy}{bot.paceName ? ` · ${bot.paceName}` : ''}</b></div>
          {bot.gridLower ? (
            <div style={styles.muted}>Rango{bot.gridAuto ? ' automático' : ''}: <b style={{ color: '#c9d3ee' }}>${bot.gridLower.toLocaleString('en-US')} – ${bot.gridUpper.toLocaleString('en-US')} · {bot.gridCount} grids</b></div>
          ) : bot.gridAuto && (
            <div style={styles.muted}>Rango: <b style={{ color: '#c9d3ee' }}>automático, precio ±15% al levantarlo</b></div>
          )}
        </div>
        <span style={styles.pill(running)}>● {running ? 'CORRIENDO' : 'DETENIDO'}</span>
      </div>

      <div style={styles.action}>
        <span>⏳ Última acción: <b style={{ color: '#fff' }}>{running ? (summary ? summary.action : 'Iniciando…') : 'Bot detenido'}</b></span>
        {running && summary && summary.diffPct !== null && (
          <span style={{ whiteSpace: 'nowrap' }}>Diff: {summary.diffPct > 0 ? '+' : ''}{summary.diffPct}%</span>
        )}
      </div>

      <div style={styles.footer}>
        <button style={dim(styles.button('#c9d3ee', '#2a3558'))} disabled={disabled} onClick={() => onView(bot)}>👁 Ver Matriz / Logs</button>
        {running && onStop && (
          <button style={dim(styles.button(RED, RED))} disabled={disabled} onClick={() => onStop(bot)}>{working ? 'Deteniendo…' : '⏹ Detener'}</button>
        )}
        {!running && onStart && (
          <button style={dim(styles.button(GREEN, GREEN))} disabled={disabled} onClick={() => onStart(bot)}>{working ? 'Levantando…' : '▶ Levantar'}</button>
        )}
        {onDelete && (
          <button
            style={{ ...styles.button(running ? '#5b6788' : GREEN, 'transparent'), marginLeft: 'auto', fontSize: 16, background: 'transparent', opacity: running || disabled ? 0.4 : 1, cursor: running || disabled ? 'not-allowed' : 'pointer' }}
            disabled={running || disabled}
            title={running ? 'Detén el bot para poder eliminarlo' : 'Eliminar bot'}
            onClick={() => onDelete(bot)}
          >🗑</button>
        )}
      </div>
    </div>
  );
};

const BotGrid = ({ bots, ...handlers }) => (
  <Row>
    {bots.map(bot => (
      <Col key={bot.id} lg="4" md="6" style={{ marginBottom: 20 }}>
        <BotCard bot={bot} {...handlers} />
      </Col>
    ))}
  </Row>
);

// Bots del usuario: crear (moneda + monto + estrategia), levantar, detener y eliminar
const Bots = ({ bots, otherBots = [], reload, onSelect, onBilling }) => {
  const [coins, setCoins] = useState([]);
  const [minAmount, setMinAmount] = useState(15);
  const [coin, setCoin] = useState("");
  const [amount, setAmount] = useState("");
  const [compound, setCompound] = useState(false);
  const [compoundCap, setCompoundCap] = useState("");
  const [strategies, setStrategies] = useState([]);
  const [strategy, setStrategy] = useState("");
  const [gridLower, setGridLower] = useState("");
  const [gridUpper, setGridUpper] = useState("");
  const [gridCount, setGridCount] = useState("33");
  const [gridManual, setGridManual] = useState(false);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [billing, setBilling] = useState(null);
  const superAdmin = isSuperAdmin();

  useEffect(() => {
    async function fetchCoins() {
      const res = await ApiCall({ call: `coins`, method: "GET" });
      if (res && res.coins) {
        setCoins(res.coins);
        setMinAmount(res.minAmount);
        setStrategies(res.strategies || []);
        setStrategy(res.defaultStrategy || "");
      }
    }
    async function fetchBilling() {
      const res = await ApiCall({ call: `billing`, method: "GET" });
      if (res && !res.error) setBilling(res);
    }
    fetchCoins();
    fetchBilling();
  }, []);

  // Con interés compuesto el tope tiene que superar el monto
  const capInvalid = compound && !(Number(compoundCap) > Number(amount));

  // Grid: rango y número de grids; el paso sale de ahí (geométrico) y la comisión de ida y vuelta es 0.2%
  const isGrid = strategy === 'grid';
  const gl = Number(gridLower), gu = Number(gridUpper), gc = Number(gridCount);
  const gridStep = gl > 0 && gu > gl && gc >= 2 ? (Math.pow(gu / gl, 1 / gc) - 1) * 100 : null;
  const gridInvalid = isGrid && gridManual && (gridStep === null || gc > 150 || gridStep < 0.25);

  // Comisión por pagar: la vencida bloquea crear y levantar bots
  const due = billing ? billing.invoices.find(i => i.status === 'pending') : null;

  // Un bot por moneda: las ya usadas no se ofrecen (el super admin puede repetir)
  const used = bots.map(b => b.coin);
  const available = superAdmin ? coins : coins.filter(c => !used.includes(c.coin));

  const run = async (id, request, okText) => {
    setBusy(id);
    setMessage(null);
    const res = await ApiCall(request);
    setBusy(null);
    if (!res) {
      setMessage({ color: 'danger', text: 'No se pudo conectar con el servidor' });
    } else if (res.error) {
      setMessage({ color: 'danger', text: res.error });
    } else if (res.liquidated === false) {
      setMessage({ color: 'warning', text: 'Bot detenido, pero no se pudo vender la posición abierta. Se reintentará al levantarlo.' });
    } else {
      setMessage({ color: 'success', text: okText });
    }
    await reload();
    return res;
  };

  const create = async () => {
    const params = { coin, amount: Number(amount), strategy, compoundCap: compound ? Number(compoundCap) : null };
    if (isGrid) Object.assign(params, gridManual ? { gridAuto: false, gridLower: gl, gridUpper: gu, gridCount: gc } : { gridAuto: true });
    const res = await run('new', { call: 'bots', params, method: 'POST' }, 'Bot creado');
    if (res && !res.error) {
      setCoin("");
      setAmount("");
      setCompound(false);
      setCompoundCap("");
      setGridLower("");
      setGridUpper("");
      setGridCount("33");
      setGridManual(false);
    }
  };

  const stop = (bot, owner) => {
    if (!window.confirm(`Detener el bot de ${bot.coin}${owner ? ` del usuario ${owner}` : ''}? Si tiene una posición abierta se venderá a precio de mercado.`)) return;
    run(bot.id, { call: `bots/${bot.id}/stop`, method: 'POST' }, `Bot de ${bot.coin} detenido`);
  };

  const remove = (bot) => {
    if (!window.confirm(`Eliminar el bot de ${bot.coin}? Se borra también su historial.`)) return;
    run(bot.id, { call: `bots/${bot.id}`, method: 'DELETE' }, `Bot de ${bot.coin} eliminado`);
  };

  const start = (bot) => run(bot.id, { call: `bots/${bot.id}/start`, method: 'POST' }, `Bot de ${bot.coin} levantado`);
  const view = (bot) => onSelect(bot.id);

  // Bots ajenos agrupados por dueno
  const groups = [];
  otherBots.forEach(bot => {
    let group = groups.find(g => g.userId === bot.userId);
    if (!group) {
      group = { userId: bot.userId, login: bot.userLogin, bots: [] };
      groups.push(group);
    }
    group.bots.push(bot);
  });

  return (
    <>
    <Card className="shadow" style={{ marginTop: 20 }}>
      <CardHeader className="border-0">
        <h3 className="mb-0">🤖 Mis bots</h3>
      </CardHeader>
      <CardBody>
        {message && <Alert color={message.color} toggle={() => setMessage(null)}>{message.text}</Alert>}
        {due && (
          <Alert color={billing.blocked ? 'danger' : 'warning'}>
            {billing.blocked
              ? 'Tienes una comisión vencida: no puedes crear, levantar ni eliminar bots hasta reportar el pago. '
              : `Tienes una comisión de ${due.amount} USDT por pagar antes del ${due.dueDate.slice(0, 10)}. `}
            <a href="#billing" style={{ color: '#fff', textDecoration: 'underline' }} onClick={e => { e.preventDefault(); onBilling(); }}>Ir a Comisiones</a>
          </Alert>
        )}

        <Row className="align-items-end mb-4">
          <Col md="3">
            <small className="text-muted">Moneda</small>
            <Input type="select" value={coin} onChange={e => setCoin(e.target.value)}>
              <option value="">Seleccionar…</option>
              {available.map(c => <option key={c.coin} value={c.coin}>{c.name} ({c.coin})</option>)}
            </Input>
          </Col>
          <Col md="3">
            <small className="text-muted">Monto para tradear (USDT, mínimo {minAmount})</small>
            <Input type="number" min={minAmount} value={amount} onChange={e => setAmount(e.target.value)} placeholder={`${minAmount}`} />
          </Col>
          <Col md="3">
            <small className="text-muted">Estrategia</small>
            <Input type="select" value={strategy} onChange={e => setStrategy(e.target.value)}>
              {strategies.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
            </Input>
          </Col>
          <Col md="3">
            <Button color="primary" disabled={!coin || Number(amount) < minAmount || capInvalid || gridInvalid || busy !== null} onClick={create}>
              {busy === 'new' ? 'Creando…' : 'Crear bot'}
            </Button>
          </Col>
        </Row>
        <Row className="align-items-end mb-4" style={{ marginTop: -12 }}>
          <Col md="3">
            <label className="text-muted" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={compound} onChange={e => setCompound(e.target.checked)} style={{ marginRight: 6 }} />
              Interés compuesto
            </label>
          </Col>
          {compound && (
            <Col md="6">
              <small className="text-muted">Tope (USDT): suma la ganancia al monto hasta este límite; nunca opera con menos que el monto</small>
              <Input type="number" min={Number(amount) || minAmount} value={compoundCap} onChange={e => setCompoundCap(e.target.value)}
                     placeholder={amount ? `${Number(amount) * 2}` : ''} invalid={capInvalid && compoundCap !== ''} />
            </Col>
          )}
        </Row>
        {isGrid && (
          <Row className="align-items-end mb-2" style={{ marginTop: -12 }}>
            <Col md="12">
              <label className="text-muted" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={gridManual} onChange={e => setGridManual(e.target.checked)} style={{ marginRight: 6 }} />
                Configurar el rango a mano
              </label>
              {!gridManual && (
                <small className="text-muted" style={{ marginLeft: 12 }}>
                  Automático: al levantar el bot el rango es el precio ±15% con líneas cada 2% (15 grids). Mínimo ~96 USDT.
                </small>
              )}
            </Col>
          </Row>
        )}
        {isGrid && gridManual && (
          <Row className="align-items-end mb-4">
            <Col md="3">
              <small className="text-muted">Precio inferior (USDT)</small>
              <Input type="number" min={0} value={gridLower} onChange={e => setGridLower(e.target.value)} placeholder="75000" />
            </Col>
            <Col md="3">
              <small className="text-muted">Precio superior (USDT)</small>
              <Input type="number" min={0} value={gridUpper} onChange={e => setGridUpper(e.target.value)} placeholder="85000"
                     invalid={gridUpper !== '' && gridLower !== '' && !(gu > gl)} />
            </Col>
            <Col md="2">
              <small className="text-muted">Número de grids</small>
              <Input type="number" min={2} max={150} value={gridCount} onChange={e => setGridCount(e.target.value)} />
            </Col>
            <Col md="4">
              <small className="text-muted">
                {gridStep === null
                  ? 'Geométrico: mismo % entre líneas. Fuera del rango no opera.'
                  : `Paso ${gridStep.toFixed(2)}% · ganancia por vuelta ${(gridStep - 0.2).toFixed(2)}% después de comisión`
                    + (gridStep < 0.25 ? ' · muy chico: usa menos grids' : '')}
              </small>
            </Col>
          </Row>
        )}
        {!superAdmin && coins.length > 0 && available.length === 0 && (
          <p className="text-muted">Ya tienes un bot de cada moneda disponible.</p>
        )}

        {bots.length === 0
          ? <p className="text-center text-muted">Aún no tienes bots. Crea el primero arriba.</p>
          : <BotGrid bots={bots} busy={busy} onView={view} onStart={start} onStop={(bot) => stop(bot)} onDelete={remove} />
        }
      </CardBody>
    </Card>

    {superAdmin && (
      <Card className="shadow" style={{ marginTop: 20, marginBottom: 20 }}>
        <CardHeader className="border-0">
          <h3 className="mb-0">👥 Bots de otros usuarios</h3>
        </CardHeader>
        <CardBody>
          {groups.length === 0 && <p className="text-center text-muted">Ningún otro usuario ha creado bots.</p>}
          {groups.map(group => (
            <div key={group.userId} style={styles.group}>
              <div style={styles.groupTitle}>
                👤 {group.login} <span style={styles.muted}>· {group.bots.length} {group.bots.length === 1 ? 'bot' : 'bots'}</span>
              </div>
              <BotGrid bots={group.bots} busy={busy} onView={view} onStop={(bot) => stop(bot, group.login)} />
            </div>
          ))}
        </CardBody>
      </Card>
    )}
    </>
  );
};

export default Bots;
